/**
 * Web Vitals Performance Monitoring
 *
 * Tracks Core Web Vitals metrics and sends them to analytics/monitoring services.
 * Metrics tracked: LCP, FID, CLS, FCP, TTFB
 */

import { onCLS, onFCP, onLCP, onTTFB, type Metric } from 'web-vitals'
import { env } from '@/config/env'
import { analytics } from './analytics'
import { addBreadcrumb } from './error-tracking'

/**
 * Web Vitals thresholds (Google recommendations)
 */
const THRESHOLDS = {
  // Largest Contentful Paint (LCP) - loading performance
  LCP: {
    good: 2500, // 2.5s
    poor: 4000, // 4.0s
  },
  // First Input Delay (FID) - interactivity
  FID: {
    good: 100, // 100ms
    poor: 300, // 300ms
  },
  // Cumulative Layout Shift (CLS) - visual stability
  CLS: {
    good: 0.1,
    poor: 0.25,
  },
  // First Contentful Paint (FCP) - perceived load speed
  FCP: {
    good: 1800, // 1.8s
    poor: 3000, // 3.0s
  },
  // Time to First Byte (TTFB) - server response time
  TTFB: {
    good: 800, // 800ms
    poor: 1800, // 1.8s
  },
}

/**
 * Determine rating based on threshold
 */
function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as keyof typeof THRESHOLDS]
  if (!threshold) return 'good'

  if (value <= threshold.good) return 'good'
  if (value <= threshold.poor) return 'needs-improvement'
  return 'poor'
}

/**
 * Send metric to analytics and monitoring services
 */
function sendMetric(metric: Metric): void {
  const { name, value, rating, id, navigationType } = metric

  // Round to 2 decimal places
  const roundedValue = Math.round(value * 100) / 100

  if (env.IS_DEV) {
    console.log(`[performance] ${name}:`, {
      value: roundedValue,
      rating,
      id,
      navigationType,
    })
  }

  // Add breadcrumb for Sentry
  addBreadcrumb(`Web Vital: ${name}`, {
    value: roundedValue,
    rating,
    navigationType: navigationType || 'unknown',
  })

  // Send to analytics (if enabled)
  if (env.ENABLE_ANALYTICS) {
    analytics.action('Web Vital', {
      metric: name,
      value: roundedValue,
      rating: rating || getRating(name, value),
      navigationType: navigationType || 'unknown',
    })
  }

  // Warn if performance is poor
  if (rating === 'poor' || getRating(name, value) === 'poor') {
    console.warn(`[performance] Poor ${name}: ${roundedValue} (threshold: ${THRESHOLDS[name as keyof typeof THRESHOLDS]?.poor})`)
  }
}

/**
 * Initialize Web Vitals monitoring
 * Call this once at app startup
 */
export function initPerformanceMonitoring(): void {
  if (typeof window === 'undefined') {
    return // Skip on server
  }

  if (env.IS_DEV) {
    console.log('[performance] Web Vitals monitoring initialized')
  }

  // Track all Core Web Vitals
  onLCP(sendMetric) // Largest Contentful Paint
  onCLS(sendMetric) // Cumulative Layout Shift
  onFCP(sendMetric) // First Contentful Paint
  onTTFB(sendMetric) // Time to First Byte

  // Note: FID (First Input Delay) has been deprecated in favor of INP (Interaction to Next Paint)
  // INP is now part of Core Web Vitals but requires newer browser support
  // We'll track it if available
  import('web-vitals').then((vitals) => {
    if ('onINP' in vitals) {
      vitals.onINP(sendMetric)
    }
  }).catch(() => {
    // Ignore if web-vitals doesn't have INP yet
  })
}

/**
 * Performance marks for custom measurements
 */
export const performanceMark = {
  /**
   * Mark the start of an operation
   */
  start: (name: string): void => {
    if (typeof performance === 'undefined') return
    performance.mark(`${name}-start`)
  },

  /**
   * Mark the end of an operation and measure duration
   */
  end: (name: string): number | undefined => {
    if (typeof performance === 'undefined') return undefined

    const startMark = `${name}-start`
    const endMark = `${name}-end`

    performance.mark(endMark)

    try {
      const measure = performance.measure(name, startMark, endMark)
      const duration = Math.round(measure.duration)

      if (env.IS_DEV) {
        console.log(`[performance] ${name}: ${duration}ms`)
      }

      // Clean up marks
      performance.clearMarks(startMark)
      performance.clearMarks(endMark)
      performance.clearMeasures(name)

      return duration
    } catch (error) {
      console.warn(`[performance] Failed to measure ${name}:`, error)
      return undefined
    }
  },
}

/**
 * Report long tasks (>50ms) that block the main thread
 */
export function reportLongTasks(): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return
  }

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const duration = Math.round(entry.duration)

        if (duration > 50) {
          console.warn(`[performance] Long task detected: ${duration}ms`)

          addBreadcrumb('Long Task', {
            duration,
            startTime: entry.startTime,
          })

          if (env.ENABLE_ANALYTICS) {
            analytics.action('Long Task', {
              duration,
            })
          }
        }
      }
    })

    observer.observe({ entryTypes: ['longtask'] })
  } catch (_error) {
    // PerformanceObserver may not support longtask
    console.debug('[performance] Long task monitoring not supported')
  }
}
